import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import { useTracking } from '../contexts/TrackingContext';
import LanguageSwitcher from '../i18n/LanguageSwitcher';

interface HeaderProps {
  onToggleSidebar: () => void;
}

/**
 * Header component with navigation and user authentication controls
 */
const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { t } = useTranslation(['navigation', 'common', 'auth']);
  const { user, isAuthenticated } = useAuth();
  const { user: partnerUser } = usePartnerAuth();
  const { activeSession, isOwnSession, activeSegments, isSending, handleChangeTarget, handleStopSession, targetUpdating } = useTracking();
  const location = useLocation();
  const isPublicLivePage = location.pathname.startsWith('/live/');
  const isTrackingPage = location.pathname.startsWith('/live/');

  // Determine if user is a partner
  const isPartnerUser = partnerUser !== null;

  return (
    <header style={headerStyle}>
      <div style={containerStyle}>
        {/* Left section: Hamburger + Logo */}
        <div style={leftSectionStyle}>
          {/* Hamburger Menu Button – hidden on tracking pages */}
          {!isTrackingPage && (
            <button
              onClick={onToggleSidebar}
              style={hamburgerButtonStyle}
              className="hamburger-button"
              aria-label="Toggle menu"
            >
              <span style={hamburgerLineStyle}></span>
              <span style={hamburgerLineStyle}></span>
              <span style={hamburgerLineStyle}></span>
            </button>
          )}

          {/* Logo/Brand – on tracking pages, clicking goes to active tracking route */}
          <Link
            to={isTrackingPage && activeSession ? `/live/${activeSession.token}` : '/'}
            style={logoStyle}
          >
            {t('navigation:brand')}
          </Link>

          {/* Page title next to brand */}
          {(() => {
            const pageTitles: Record<string, string> = {
              '/upcoming-matches': 'Kommende Spiele',
              '/matchday-planning': 'Spieltagsplanung',
              '/profile': 'Profil',
              '/sports': 'Sportarten',
              '/sports-management': 'Sportverwaltung',
              '/tracking': 'Tracking',
              '/countries': 'Länder',
              '/leagues': 'Ligen',
              '/clubs': 'Vereine',
              '/seasons': 'Saisons',
              '/competitions': 'Wettbewerbe',
              '/matchdays': 'Spieltage',
              '/matches': 'Spiele',
              '/local-matches': 'Lokale Spiele',
              '/local-matchdays': 'Lokale Spieltage',
              '/partners': 'Partner',
              '/custom-partners': 'Eigene Partner',
              '/partner-access': 'Partnerzugang',
              '/travelers': 'Reisende',
              '/drivers': 'Reisende',
              '/players': 'Reisende',
              '/transportation': 'Transport',
              '/user-management': 'Benutzerverwaltung',
              '/data-management': 'Datenverwaltung',
            };
            // Check for exact match first, then prefix match
            const title = pageTitles[location.pathname] ||
              Object.entries(pageTitles).find(([path]) => location.pathname.startsWith(path))?.[1];
            // Also show "Kommende Spiele" on home page for authenticated users
            const showHomeTitle = location.pathname === '/' && isAuthenticated;
            const displayTitle = title || (showHomeTitle ? 'Kommende Spiele' : null);
            return displayTitle ? (
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 400, marginLeft: '12px' }}>
                | {displayTitle}
              </span>
            ) : null;
          })()}
        </div>

        {/* Tracking Widget – nur auf internen Seiten, nicht auf öffentlicher Live-Seite */}
        {activeSession && isAuthenticated && !isPublicLivePage && (
          <div style={trackingWidgetStyle}>
            <span style={{color: isOwnSession && isSending ? '#2ecc71' : isOwnSession ? '#e74c3c' : '#ffc107', marginRight: '6px'}}>●</span>
            <span style={{fontSize: '0.75rem', opacity: 0.85, marginRight: '8px'}}>
              LIVE{!isOwnSession && activeSession.deviceName ? ` (${activeSession.deviceName})` : ''}
            </span>
            {isOwnSession && (
              <>
                <select
                  value={activeSession.targetSegmentId ?? ''}
                  onChange={(e) => handleChangeTarget(e.target.value ? Number(e.target.value) : null)}
                  disabled={targetUpdating}
                  style={trackingSelectStyle}
                >
                  <option value="">– Ziel wählen –</option>
                  {activeSegments.map(seg => (
                    <option key={seg.id} value={seg.id}>
                      {seg.sequenceOrder}. {seg.location}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleStopSession}
                  title="Tracking beenden"
                  style={trackingStopBtnStyle}
                >
                  ■
                </button>
              </>
            )}
          </div>
        )}

        {/* Right section: Language Switcher + User Info */}
        <div style={rightSectionStyle}>
          {/* Language Switcher */}
          <LanguageSwitcher />

          {!isTrackingPage && (isAuthenticated || isPartnerUser) && (
            <Link to={isPartnerUser ? "/partner/dashboard" : "/profile"} style={userInfoStyle} className="user-info-link">
              {isPartnerUser ? (partnerUser?.displayName || partnerUser?.email) : (user?.displayName || user?.email)}
            </Link>
          )}
        </div>
      </div>

    </header>
  );
};

// Styles
const headerStyle: React.CSSProperties = {
  backgroundColor: '#2c3e50',
  color: 'white',
  padding: '0',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
};

const containerStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const logoStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: 'white',
  textDecoration: 'none',
};

const trackingWidgetStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(255,255,255,0.1)',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '0.8rem',
  color: 'white',
  whiteSpace: 'nowrap',
};

const trackingSelectStyle: React.CSSProperties = {
  backgroundColor: '#34495e',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '4px',
  padding: '2px 6px',
  fontSize: '0.75rem',
  maxWidth: '180px',
  cursor: 'pointer',
};

const trackingStopBtnStyle: React.CSSProperties = {
  backgroundColor: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '2px 8px',
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  marginLeft: '6px',
  lineHeight: 1.4,
};

const leftSectionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
};

const rightSectionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
};

const userInfoStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  opacity: 0.9,
  color: '#ecf0f1',
  fontWeight: '500',
  textDecoration: 'none',
  transition: 'opacity 0.2s',
  cursor: 'pointer',
};

const hamburgerButtonStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0.5rem',
  gap: '4px',
  borderRadius: '4px',
  transition: 'background-color 0.2s',
};

const hamburgerLineStyle: React.CSSProperties = {
  width: '24px',
  height: '3px',
  backgroundColor: 'white',
  borderRadius: '2px',
  transition: 'all 0.3s',
};



// CSS for interactive elements
const style = document.createElement('style');
style.textContent = `
  .hamburger-button:hover {
    background-color: rgba(255,255,255,0.1);
  }

  .user-info-link:hover {
    opacity: 1 !important;
    text-decoration: underline;
  }
`;
if (!document.querySelector('[data-header-styles]')) {
  style.setAttribute('data-header-styles', '');
  document.head.appendChild(style);
}

export default Header;
